<template>
  <li class="header-notification-nav" ref="csfNav">
    <button
      class="header-action-btn"
      type="button"
      @click.stop="openModal"
      title="Constancia de Situación Fiscal"
    >
      <i class="fa fa-id-card-o"></i>
    </button>
  </li>

  <teleport to="body">
    <div v-if="showModal" class="csf-modal-backdrop" @click="closeModal">
      <div class="csf-modal" role="dialog" aria-modal="true" @click.stop>
        <div v-if="processing" class="csf-processing-overlay">
          <div class="glo-card csf-processing-card">
            <div class="spinner-border" role="status" aria-hidden="true"></div>
            <div class="glo-text csf-processing-text"><img src="/minilogo.png" alt="TusCFDIs" class="csf-processing-logo" /></div>
          </div>
        </div>

        <div class="csf-modal-header">
          <div>
            <h5 class="csf-modal-title">Constancia de Situación Fiscal</h5>
            <small class="csf-meta" v-if="latest && latest.generated_at">
              Última generación: {{ formatDate(latest.generated_at) }}
            </small>
            <small class="csf-meta" v-else>
              Aún no se ha generado la constancia de situación fiscal para este RFC.
            </small>
          </div>
          <button class="csf-close-btn" type="button" @click="closeModal" aria-label="Cerrar">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <div class="csf-modal-body">
          <div v-if="loading" class="csf-state-msg">Cargando constancia...</div>

          <div v-else-if="!currentRfc" class="csf-state-msg">
            Selecciona un RFC para consultar o generar la constancia.
          </div>

          <div v-else-if="!latest" class="csf-empty-wrap">
            <img src="/onboarding/csf.png" alt="Constancia de Situación Fiscal" class="csf-empty-image" />
          </div>

          <div v-else-if="previewUrl" class="csf-preview-wrap">
            <iframe
              :src="previewUrl"
              class="csf-preview-frame"
              title="Vista previa de constancia"
            ></iframe>
          </div>

          <div v-else class="csf-state-msg">
            No hay PDF disponible para previsualizar. Puedes generar una nueva constancia.
          </div>

          <small v-if="error" class="csf-error">{{ error }}</small>
        </div>

        <div class="csf-modal-footer">
          <button
            type="button"
            class="csf-option-btn"
            :disabled="processing || !currentRfc"
            @click="generateCertificate"
          >
            {{ processing ? 'Generando...' : 'Generar Constancia' }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import taxStatusCertificateService from '@/services/taxStatusCertificate';

export default {
  name: 'TaxStatusCertificateHeaderAction',
  data() {
    return {
      showModal: false,
      loading: false,
      processing: false,
      latest: null,
      error: '',
      previewUrl: '',
    };
  },
  computed: {
    currentRfc() {
      return String(localStorage.getItem('rfc') || '').trim().toUpperCase();
    },
  },
  methods: {
    extractPayload(response) {
      const isAxiosRaw = !!(response && typeof response === 'object' && 'status' in response && 'config' in response);
      return isAxiosRaw ? response.data : response;
    },
    emitHeaderDropdownOpen(source) {
      window.dispatchEvent(new CustomEvent('header-dropdown-open', { detail: { source } }));
    },
    handleHeaderDropdownOpen(event) {
      const source = event && event.detail ? event.detail.source : '';
      if (source !== 'csf') {
        this.showModal = false;
      }
    },
    async openModal() {
      if (this.showModal) {
        return;
      }

      this.showModal = true;
      this.emitHeaderDropdownOpen('csf');
      await this.loadLatest();
    },
    closeModal() {
      if (this.processing) {
        return;
      }

      this.showModal = false;
    },
    async loadLatest() {
      this.loading = true;
      this.error = '';
      this.latest = null;
      this.previewUrl = '';

      if (!this.currentRfc) {
        this.loading = false;
        this.error = 'Selecciona un RFC para consultar la constancia.';
        return;
      }

      try {
        const response = await taxStatusCertificateService.getLatest(this.currentRfc);
        const payload = this.extractPayload(response);
        this.latest = payload?.data?.certificate || null;

        if (this.latest?.id) {
          await this.loadPreview(this.latest.id);
        }
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo consultar la constancia actual.';
      }

      this.loading = false;
    },
    async generateCertificate() {
      if (!this.currentRfc || this.processing) {
        return;
      }

      this.processing = true;
      this.error = '';

      try {
        const response = await taxStatusCertificateService.generate(this.currentRfc);
        const payload = this.extractPayload(response);
        this.latest = payload?.data?.certificate || null;

        if (this.latest?.id) {
          await this.loadPreview(this.latest.id);
        }
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo generar la constancia.';
      }

      this.processing = false;
    },
    async loadPreview(certificateId = null) {
      if (!this.currentRfc) {
        return;
      }

      try {
        const response = await taxStatusCertificateService.getPreview(this.currentRfc, certificateId);
        const payload = this.extractPayload(response);
        this.previewUrl = payload?.data?.preview_data_url || '';
      } catch (error) {
        this.previewUrl = '';
        this.error = error?.response?.data?.message || 'No se pudo cargar la previsualización de la constancia.';
      }
    },
    async downloadLatest() {
      if (!this.currentRfc || !this.latest || this.processing) {
        return;
      }

      this.processing = true;
      this.error = '';

      try {
        const response = await taxStatusCertificateService.getDownloadLink(this.currentRfc, this.latest.id);
        const payload = this.extractPayload(response);
        const url = payload?.data?.download_url || '';

        if (!url) {
          this.error = 'No se encontró URL de descarga para la constancia.';
        } else {
          window.open(url, '_blank');
        }
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo descargar la constancia.';
      }

      this.processing = false;
    },
    formatDate(dateValue) {
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) {
        return String(dateValue || '');
      }

      return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    handleRfcsStorageUpdated() {
      if (this.showModal) {
        this.loadLatest();
      }
    },
    handleStorageEvent(event) {
      if (!event || event.key === null || event.key === 'rfc' || event.key === 'rfcs') {
        if (this.showModal) {
          this.loadLatest();
        }
      }
    },
    handleEscapeClose(event) {
      if (!event) {
        return;
      }

      if (event.key === 'Escape' && this.showModal) {
        this.closeModal();
      }
    },
  },
  mounted() {
    window.addEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
    window.addEventListener('rfcs-storage-updated', this.handleRfcsStorageUpdated);
    window.addEventListener('storage', this.handleStorageEvent);
    window.addEventListener('keydown', this.handleEscapeClose);
  },
  beforeUnmount() {
    window.removeEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
    window.removeEventListener('rfcs-storage-updated', this.handleRfcsStorageUpdated);
    window.removeEventListener('storage', this.handleStorageEvent);
    window.removeEventListener('keydown', this.handleEscapeClose);
  },
  beforeDestroy() {
    window.removeEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
    window.removeEventListener('rfcs-storage-updated', this.handleRfcsStorageUpdated);
    window.removeEventListener('storage', this.handleStorageEvent);
    window.removeEventListener('keydown', this.handleEscapeClose);
  },
};
</script>

<style scoped>
.header-notification-nav {
  position: relative;
  display: flex;
  align-items: center;
}

.header-action-btn {
  border: 0;
  background: var(--white);
  height: 52px;
  min-height: 52px;
  min-width: 46px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid var(--sidebar-border);
  transition: all 0.15s ease;
  position: relative;
}

.header-action-btn i {
  font-size: 16px;
  color: var(--theme-font-color);
  opacity: 0.8;
}

.header-action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px var(--sidebar-border);
}

.header-action-btn:hover i {
  color: var(--theme-deafult);
  opacity: 1;
}

.csf-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(11, 22, 44, 0.48);
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.csf-modal {
  position: relative;
  width: min(940px, 100%);
  height: min(86vh, 760px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 24px 60px rgba(17, 30, 58, 0.35);
  border: 1px solid var(--sidebar-border);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.csf-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--sidebar-border);
  background: #f8fafc;
}

.csf-modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--theme-font-color);
}

.csf-close-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid var(--sidebar-border);
  background: #fff;
  color: var(--theme-font-color);
}

.csf-modal-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.csf-preview-wrap {
  flex: 1;
  min-height: 0;
  border: 1px solid var(--sidebar-border);
  border-radius: 10px;
  overflow: hidden;
  background: #fafcff;
}

.csf-preview-frame {
  width: 100%;
  height: 100%;
  border: 0;
}

.csf-state-msg {
  border: 1px dashed var(--sidebar-border);
  border-radius: 10px;
  background: #f8fafc;
  color: var(--theme-body-font-color);
  padding: 20px 16px;
}

.csf-empty-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.csf-empty-image {
  width: auto;
  height: auto;
  max-width: 80%;
  max-height: 100%;
  object-fit: contain;
}

.csf-modal-footer {
  flex-shrink: 0;
  border-top: 1px solid var(--sidebar-border);
  padding: 12px 16px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.csf-meta {
  color: var(--theme-body-font-color);
}

.csf-option-btn {
  border: 1px solid var(--theme-deafult);
  background: var(--theme-deafult);
  color: #fff;
  border-radius: 8px;
  height: 38px;
  font-size: 13px;
  font-weight: 600;
}

.csf-option-btn.secondary {
  background: #fff;
  color: var(--theme-deafult);
}

.csf-option-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.csf-error {
  color: #dc3545;
}

.csf-processing-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.csf-processing-card {
  background: #fff;
  border-radius: 10px;
  padding: 16px 18px;
  min-width: 220px;
  max-width: 80vw;
  box-shadow: 0 15px 40px rgba(15, 23, 42, 0.25);
  display: flex;
  align-items: center;
  gap: 12px;
}

.glo-text {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.csf-processing-text {
  display: flex;
  align-items: center;
}

.csf-processing-logo {
  width: 150px;
  max-width: 55vw;
}

.csf-processing-card :deep(.spinner-border) {
  width: 2rem;
  height: 2rem;
}

.csf-processing-card :deep(.spinner-border),
.csf-processing-card :deep(.spinner-border-sm) {
  color: var(--theme-deafult);
}

.csf-processing-card {
  padding: 12px 14px;
  padding: 16px 18px;
}

@media (max-width: 576px) {
  .csf-modal {
    height: min(92vh, 760px);
  }

  .csf-modal-footer {
    flex-direction: column;
  }

  .csf-option-btn {
    width: 100%;
  }
}
</style>
